import { db } from '../firebase/firebase'; 
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  Timestamp,
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
  where,
  documentId,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { User } from '../types';
import { getAuth } from 'firebase/auth';
import { FeatureFlags } from '../types';
import { UnverifiedUsersService } from '../services/unverifiedUsersService';
import { verifyEmailAsync } from '../utilities/emailEncryption';
import { getCloudFunction } from '../utilities/cloudFunctions';
import type { PlatformAdminUser } from '../types/PlatformAdminUser';
import {
  updateFacilitatorStatus as updateFacilitatorStatusForUser,
  updateWanderWriterStatus as updateWanderWriterStatusForUser,
  updateWanderWriterStatusForOrganisation as updateWanderWriterStatusForOrganisationForUser,
  isWanderWriter as checkIsWanderWriter,
  updateFacilitatorStatusForOrganisation as updateFacilitatorStatusForOrganisationForUser,
  isFacilitator as checkIsFacilitator,
  getFacilitatorInfo as fetchFacilitatorInfo,
  checkFacilitatorProfileCompleteness as fetchFacilitatorProfileCompleteness,
  syncOverhearOrganisationAdminRole,
} from './user/roles';
import type { FacilitatorInfo, FacilitatorProfileCompleteness } from './user/userOrganisations';
import {
  updateUserEmail as updateUserEmailAddress,
  updateProjectAdminEmail as updateProjectAdminEmailAddress,
  checkProjectAdminEmail as checkProjectAdminEmailAddress,
  updateEmailReminderFrequency as updateUserEmailReminderFrequency,
} from './user/email';
import { resolveDecryptedEmailFromUserData } from './base/userEmailUtils';

const FIRESTORE_IN_QUERY_LIMIT = 30;
const DEFAULT_USERS_PAGE_SIZE = 50;

export type UsersPageOrderField = 'userProfile.userName';
export type UsersPageOrderDirection = 'asc' | 'desc';

export interface GetUsersPageOptions {
  pageSize?: number;
  cursor?: QueryDocumentSnapshot<DocumentData> | null;
  orderField?: UsersPageOrderField;
  orderDirection?: UsersPageOrderDirection;
  excludeEmptyNames?: boolean;
}

export interface GetUsersPageResult {
  users: User[];
  nextCursor: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}

const chunkArray = <T>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

const applyUnverifiedStatus = async (users: User[]): Promise<User[]> => {
  try {
    const unverifiedUsers = await UnverifiedUsersService.getUnverifiedUsers();
    const unverifiedUids = new Set(unverifiedUsers.map((u) => u.uid));

    users.forEach((user) => {
      if (user.userKey && unverifiedUids.has(user.userKey)) {
        user.isUnverified = true;
      }
    });
  } catch (error) {
    console.error('Error fetching unverified users:', error);
  }

  return users;
};

export const UserManager = {
  /**
   * Fetches the current user's data from Firestore.
   * The userId should be obtained from the AuthenticationStore after successful authentication.
   * @param userId The UID of the user, as obtained from Firebase Authentication.
   * @returns A Promise that resolves to the User data or null if not found.
   */
  getCurrentUser: async (userId: string): Promise<User | null> => {
    try {
      const userDocRef = doc(db, 'Users', userId);
      const userDoc = await getDoc(userDocRef);
  
      if (!userDoc.exists()) {
        
        return null;
      }
  
      const userData = userDoc.data();
  
      return { userKey: userDoc.id, ...userData } as User;
    } catch (error) {
      console.error("Error fetching user:", error);
      return null;
    }
  },

  /**
   * Creates a new user document in Firestore.
   * @param userData The user data to be stored in Firestore.
   * @param username The username for the new user.
   * @returns A Promise that resolves to void if successful, or rejects with an error.
   */
  createUser: async (userData: Omit<User, 'userKey'> & { userKey: string }, username?: string): Promise<void> => {
    try {
      const { userKey, userProfile, ...restUserData } = userData;
      const userDocRef = doc(db, 'Users', userKey);
      
      await setDoc(userDocRef, {
        ...restUserData,
        userKey,
        userProfile: {
          userName: username ? username.trim() : userProfile.userName, // Use provided username or fallback to userProfile's userName
          bio: userProfile.bio,
          profileImage: userProfile.profileImage,
          restrictAutoAdd: userProfile.restrictAutoAdd,
          authorKey: userProfile.authorKey // Use the provided authorKey
        },
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });

      
    } catch (error) {
      console.error("Error creating user:", error);
      throw error;
    }
  },

  /**
   * Fetches a user's data from Firestore by their userKey.
   * @param userKey The unique identifier for the user.
   * @returns A Promise that resolves to the User data or null if not found.
   */
  getUserByKey: async (userKey: string): Promise<User | null> => {
    try {
      const userDocRef = doc(db, 'Users', userKey);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        
        return null;
      }

      const userData = userDoc.data();

      return { userKey: userDoc.id, ...userData } as User;
    } catch (error) {
      console.error("Error fetching user by key:", error);
      return null;
    }
  },

  /**
   * Updates the user's profile in Firestore.
   * @param userId The UID of the user, as obtained from Firebase Authentication.
   * @param profileData The profile data to update.
   * @returns A Promise that resolves to void if successful, or rejects with an error.
   */
  updateUserProfile: async (userId: string, profileData: Partial<User['userProfile']>): Promise<void> => {
    try {
      const userDocRef = doc(db, 'Users', userId);
      
      // Ensure userName is not empty if it's being updated
      if ('userName' in profileData && (!profileData.userName || profileData.userName.trim() === '')) {
        throw new Error('Username cannot be empty');
      }

      // Merge into userProfile with dot paths — assigning `userProfile: partial` would replace
      // the whole map in Firestore and wipe fields not included in the partial (e.g. authorKey
      // after userName, or bio/profileImage on any single-field update).
      const updates: Record<string, unknown> = {
        updatedAt: Timestamp.now(),
      };
      (Object.entries(profileData) as [keyof User['userProfile'], User['userProfile'][keyof User['userProfile']]][]).forEach(
        ([key, value]) => {
          if (value !== undefined) {
            updates[`userProfile.${String(key)}`] = value;
          }
        }
      );

      await updateDoc(userDocRef, updates as any);

      
    } catch (error) {
      console.error("Error updating user profile:", error);
      throw error;
    }
  },

  /**
   * Updates the user's feature flags in Firestore.
   * @param userId The UID of the user.
   * @param featureFlags The feature flags to update.
   * @returns A Promise that resolves to void if successful, or rejects with an error.
   */
  updateUserFeatureFlags: async (userId: string, featureFlags: FeatureFlags): Promise<void> => {
    try {


      // Check if current user is SuperAdmin
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.error('No authenticated user found');
        throw new Error('Not authenticated');
      }

      // Check SuperAdmin status
      const superAdminRef = doc(db, 'SuperAdmins', 'ids');
      const superAdminDoc = await getDoc(superAdminRef);


      const userKeys = superAdminDoc.data()?.userKeys || [];


      if (!superAdminDoc.exists() || !userKeys.includes(currentUser.uid)) {
        console.error('Current user is not a SuperAdmin');
        throw new Error('Insufficient permissions - Not a SuperAdmin');
      }

      // First check if target user exists
      const userDocRef = doc(db, 'Users', userId);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        console.error('User document does not exist:', userId);
        throw new Error('User not found');
      }
      
      const userData = userDoc.data();


      // Only update featureFlags field
      const updateData = {
        'featureFlags': featureFlags,
        'updatedAt': Timestamp.now()
      };

      await updateDoc(userDocRef, updateData);

      // Verify the update
      const updatedDoc = await getDoc(userDocRef);

    } catch (error) {
      console.error("Error updating user feature flags:", error);
      if (error instanceof Error) {
        console.error("Error details:", {
          message: error.message,
          name: error.name,
          stack: error.stack
        });
      }
      throw error;
    }
  },

  /**
   * Fetches user keys of all super admins from Firestore (SuperAdmins/ids).
   * @returns A Promise that resolves to an array of user keys (Firebase UIDs).
   */
  getSuperAdminUserKeys: async (): Promise<string[]> => {
    try {
      const superAdminRef = doc(db, 'SuperAdmins', 'ids');
      const superAdminDoc = await getDoc(superAdminRef);
      const userKeys = superAdminDoc.data()?.userKeys || [];
      return Array.isArray(userKeys) ? userKeys.filter((k): k is string => typeof k === 'string' && k.trim().length > 0) : [];
    } catch (error) {
      console.error('Error fetching super admin user keys:', error);
      return [];
    }
  },

  /**
   * Fetches user keys of platform org admins from Firestore (OrgAdmins/ids).
   */
  getOrgAdminUserKeys: async (): Promise<string[]> => {
    try {
      const orgAdminRef = doc(db, 'OrgAdmins', 'ids');
      const orgAdminDoc = await getDoc(orgAdminRef);
      const userKeys = orgAdminDoc.data()?.userKeys || [];
      return Array.isArray(userKeys) ? userKeys.filter((k): k is string => typeof k === 'string' && k.trim().length > 0) : [];
    } catch (error) {
      console.error('Error fetching org admin user keys:', error);
      return [];
    }
  },

  /**
   * Resolves super admins and OVERHEAR platform org admins for pickers (e.g. meeting attendees).
   */
  getPlatformAdminUsers: async (): Promise<PlatformAdminUser[]> => {
    try {
      const [superAdminKeys, orgAdminKeys] = await Promise.all([
        UserManager.getSuperAdminUserKeys(),
        UserManager.getOrgAdminUserKeys(),
      ]);
      const superAdminSet = new Set(superAdminKeys);
      const allKeys = [...new Set([...superAdminKeys, ...orgAdminKeys])];

      const users = await Promise.all(
        allKeys.map(async (userKey): Promise<PlatformAdminUser | null> => {
          const user = await UserManager.getUserByKey(userKey);
          if (!user) return null;
          const resolved = await resolveDecryptedEmailFromUserData(user);
          return {
            userKey,
            userName: user.userProfile?.userName || resolved?.name || user.email || 'Unknown',
            email: resolved?.email || user.email,
            role: superAdminSet.has(userKey) ? 'superAdmin' : 'orgAdmin',
          };
        })
      );

      return users
        .filter((user): user is PlatformAdminUser => user !== null)
        .sort((a, b) => a.userName.localeCompare(b.userName));
    } catch (error) {
      console.error('Error fetching platform admin users:', error);
      return [];
    }
  },

  /**
   * Adds or removes a user from OrgAdmins/ids (OVERHEAR Admin / organisational dashboard access).
   */
  updateOrgAdminStatus: async (userId: string, isOrgAdmin: boolean): Promise<void> => {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('Not authenticated');
    }

    const superAdminRef = doc(db, 'SuperAdmins', 'ids');
    const superAdminDoc = await getDoc(superAdminRef);
    const superAdminKeys = superAdminDoc.data()?.userKeys || [];
    if (!superAdminDoc.exists() || !superAdminKeys.includes(currentUser.uid)) {
      throw new Error('Insufficient permissions - Not a SuperAdmin');
    }

    const userDocRef = doc(db, 'Users', userId);
    const userDoc = await getDoc(userDocRef);
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const orgAdminRef = doc(db, 'OrgAdmins', 'ids');
    const orgAdminDoc = await getDoc(orgAdminRef);
    const currentKeys: string[] = orgAdminDoc.exists()
      ? (orgAdminDoc.data()?.userKeys || []).filter((k: unknown): k is string => typeof k === 'string' && k.trim().length > 0)
      : [];

    if (isOrgAdmin) {
      if (!currentKeys.includes(userId)) {
        const userKeys = [...currentKeys, userId];
        if (orgAdminDoc.exists()) {
          await updateDoc(orgAdminRef, { userKeys });
        } else {
          await setDoc(orgAdminRef, { userKeys });
        }
      }
      await syncOverhearOrganisationAdminRole(userId, userDoc.data() || {}, true);
      return;
    }

    if (currentKeys.includes(userId) && orgAdminDoc.exists()) {
      const userKeys = currentKeys.filter((k) => k !== userId);
      await updateDoc(orgAdminRef, { userKeys });
    }
    await syncOverhearOrganisationAdminRole(userId, userDoc.data() || {}, false);
  },

  /**
   * Fetches all users from Firestore.
   * @returns A Promise that resolves to an array of User data.
   */
  getAllUsers: async (): Promise<User[]> => {
    try {
      const usersCollectionRef = collection(db, 'Users');
      const usersSnapshot = await getDocs(usersCollectionRef);
      const users: User[] = usersSnapshot.docs.map((userDoc) => ({
        userKey: userDoc.id,
        ...userDoc.data(),
      } as User));

      return applyUnverifiedStatus(users);
    } catch (error) {
      console.error("Error fetching all users:", error);
      return [];
    }
  },

  /**
   * Fetches a page of users ordered by display name.
   * Documents missing `userProfile.userName` are excluded by Firestore ordering.
   * Empty names sort first in Firestore, so they are skipped by default.
   */
  getUsersPage: async ({
    pageSize = DEFAULT_USERS_PAGE_SIZE,
    cursor = null,
    orderField = 'userProfile.userName',
    orderDirection = 'asc',
    excludeEmptyNames = true,
  }: GetUsersPageOptions = {}): Promise<GetUsersPageResult> => {
    try {
      const usersCollectionRef = collection(db, 'Users');
      const constraints = [
        ...(excludeEmptyNames ? [where(orderField, '>', '')] : []),
        orderBy(orderField, orderDirection),
        ...(cursor ? [startAfter(cursor)] : []),
        limit(pageSize + 1),
      ];
      const usersQuery = query(usersCollectionRef, ...constraints);
      const usersSnapshot = await getDocs(usersQuery);
      const hasMore = usersSnapshot.docs.length > pageSize;
      const pageDocs = hasMore
        ? usersSnapshot.docs.slice(0, pageSize)
        : usersSnapshot.docs;
      const users: User[] = pageDocs.map((userDoc) => ({
        userKey: userDoc.id,
        ...userDoc.data(),
      } as User));
      const usersWithUnverified = await applyUnverifiedStatus(users);

      return {
        users: usersWithUnverified,
        nextCursor: pageDocs.length > 0 ? pageDocs[pageDocs.length - 1] : null,
        hasMore,
      };
    } catch (error) {
      console.error('Error fetching users page:', error);
      return { users: [], nextCursor: null, hasMore: false };
    }
  },

  /**
   * Fetches specific users by document ID (batched `in` queries).
   */
  getUsersByKeys: async (userKeys: string[]): Promise<User[]> => {
    const uniqueKeys = [...new Set(userKeys.filter((key) => key.trim().length > 0))];
    if (uniqueKeys.length === 0) {
      return [];
    }

    try {
      const usersCollectionRef = collection(db, 'Users');
      const chunks = chunkArray(uniqueKeys, FIRESTORE_IN_QUERY_LIMIT);
      const snapshots = await Promise.all(
        chunks.map((chunk) =>
          getDocs(query(usersCollectionRef, where(documentId(), 'in', chunk)))
        )
      );
      const users: User[] = snapshots.flatMap((snapshot) =>
        snapshot.docs.map((userDoc) => ({
          userKey: userDoc.id,
          ...userDoc.data(),
        } as User))
      );

      return applyUnverifiedStatus(users);
    } catch (error) {
      console.error('Error fetching users by keys:', error);
      return [];
    }
  },

  /**
   * Builds a map of normalised email (lowercase) to userKey for all users.
   * Used to link subscribers (by decrypted email) to app accounts.
   * Uses User.email when present, otherwise decrypts Organisations[orgId].email.
   */
  getUserEmailToKeyMap: async (): Promise<Map<string, string>> => {
    const map = new Map<string, string>();
    try {
      const users = await UserManager.getAllUsers();
      for (const user of users) {
        const userKey = user.userKey;
        if (!userKey) continue;
        const userData = user as User & { email?: string; Organisations?: Record<string, { email?: string }> };
        const email = userData.email || '';
        if (email && typeof email === 'string' && email.includes('@')) {
          map.set(email.trim().toLowerCase(), userKey);
          continue;
        }
        const orgs = userData.Organisations || {};
        for (const orgId of Object.keys(orgs)) {
          const orgEmail = orgs[orgId]?.email;
          if (!orgEmail || typeof orgEmail !== 'string') continue;
          try {
            const decrypted = await verifyEmailAsync(orgEmail);
            if (decrypted && decrypted.includes('@')) {
              map.set(decrypted.trim().toLowerCase(), userKey);
              break;
            }
          } catch {
            // Skip failed decryption
          }
        }
      }
      return map;
    } catch (error) {
      console.error('Error building user email map:', error);
      return map;
    }
  },

  /**
   * Updates a user's facilitator status. If the user is not already a facilitator,
   * it adds the facilitator role to a default/global organisation. If the user is
   * already a facilitator, it removes the facilitator role.
   * @param userId The user ID to update
   * @param isFacilitator Whether the user should be a facilitator
   * @returns A Promise that resolves to void if successful, or rejects with an error
   */
  updateFacilitatorStatus: async (userId: string, isFacilitator: boolean): Promise<void> => {
    return updateFacilitatorStatusForUser(userId, isFacilitator);
  },

  /**
   * @deprecated Use updateWanderWriterStatusForOrganisation instead.
   * This method is kept for backward compatibility but should not be used for new implementations.
   * Wander writers should be stored in the Organisations field with the 'wanderWriter' role.
   */
  updateWanderWriterStatus: async (userId: string, isWanderWriter: boolean): Promise<void> => {
    return updateWanderWriterStatusForUser(userId, isWanderWriter);
  },

  /**
   * Updates a user's wander writer status for a specific organisation.
   * @param userId The user ID to update
   * @param isWanderWriter Whether the user should be a wander writer
   * @param organisationId The organisation ID to assign the wander writer role to (required when setting to wander writer)
   * @returns A Promise that resolves to void if successful, or rejects with an error
   */
  updateWanderWriterStatusForOrganisation: async (userId: string, isWanderWriter: boolean, organisationId?: string): Promise<void> => {
    return updateWanderWriterStatusForOrganisationForUser(userId, isWanderWriter, organisationId);
  },

  /**
   * Checks if a user has wander writer status in any organisation
   * @param userId The user ID to check
   * @returns A Promise that resolves to boolean indicating wander writer status
   */
  isWanderWriter: async (userId: string): Promise<boolean> => {
    return checkIsWanderWriter(userId);
  },

  /**
   * Updates a user's facilitator status for a specific organisation.
   * @param userId The user ID to update
   * @param isFacilitator Whether the user should be a facilitator
   * @param organisationId The organisation ID to assign the facilitator role to (required when setting to facilitator)
   * @returns A Promise that resolves to void if successful, or rejects with an error
   */
  updateFacilitatorStatusForOrganisation: async (userId: string, isFacilitator: boolean, organisationId?: string): Promise<void> => {
    return updateFacilitatorStatusForOrganisationForUser(userId, isFacilitator, organisationId);
  },

  /**
   * Checks if a user has facilitator status in any organisation
   * @param userId The user ID to check
   * @returns A Promise that resolves to boolean indicating facilitator status
   */
  isFacilitator: async (userId: string): Promise<boolean> => {
    return checkIsFacilitator(userId);
  },

  /**
   * Gets facilitator compliance information for a user
   * Checks Organisations[orgId].facilitatorInfo first, then falls back to root facilitatorInfo
   * @param userId The user ID to get facilitator info for
   * @returns A Promise that resolves to facilitator info or null if user not found
   */
  getFacilitatorInfo: async (userId: string): Promise<FacilitatorInfo | null> => {
    return fetchFacilitatorInfo(userId);
  },

  /**
   * Updates a user's email address with consent tracking
   * For facilitators, stores the email encrypted in Organisations[orgId].email field
   * @param userId The user ID to update
   * @param email The new email address
   * @param consentVersion Optional consent version string (defaults to '1.0')
   * @returns A Promise that resolves to void if successful, or rejects with an error
   */
  updateUserEmail: async (userId: string, email: string, consentVersion: string = '1.0'): Promise<void> => {
    return updateUserEmailAddress(userId, email, consentVersion);
  },

  /**
   * Checks if a facilitator's profile is complete (has email and name)
   * Checks Organisations[orgId].email field (encrypted) instead of Users.email
   * @param userId The user ID to check
   * @returns A Promise that resolves to profile completion status and data
   */
  checkFacilitatorProfileCompleteness: async (userId: string): Promise<FacilitatorProfileCompleteness> => {
    return fetchFacilitatorProfileCompleteness(userId);
  },

  /**
   * Updates the project admin email address (encrypted) in the user document
   * @param userId The user ID to update
   * @param email The email address to encrypt and store
   * @returns A Promise that resolves to void if successful, or rejects with an error
   */
  updateProjectAdminEmail: async (userId: string, email: string): Promise<void> => {
    return updateProjectAdminEmailAddress(userId, email);
  },

  /**
   * Checks if a project admin has an email address set
   * @param userId The user ID to check
   * @returns A Promise that resolves to an object with hasEmail flag and optionally the decrypted email
   */
  checkProjectAdminEmail: async (projectId: string, userEmail: string): Promise<{ hasEmail: boolean; email?: string }> => {
    return checkProjectAdminEmailAddress(projectId, userEmail);
  },

  /**
   * Updates the user's email reminder frequency preference
   * @param userId The UID of the user
   * @param frequency The reminder frequency ('none' | 'weekly' | 'fortnightly' | 'monthly')
   * @returns A Promise that resolves to void if successful, or rejects with an error
   */
  updateEmailReminderFrequency: async (userId: string, frequency: 'none' | 'weekly' | 'fortnightly' | 'monthly'): Promise<void> => {
    return updateUserEmailReminderFrequency(userId, frequency);
  },

  /**
   * Deletes a user from both Firestore and Firebase Auth
   * Uses cloud function to handle all deletions (Auth, Users, unverifiedUsers)
   * @param userKey The user key to delete
   * @returns A Promise that resolves to void if successful, or rejects with an error
   */
  deleteUser: async (userKey: string): Promise<void> => {
    try {
      // Use cloud function to delete from both Firebase Auth and Firestore
      // The cloud function handles cases where user doesn't exist in Auth
      const deleteUserFunction = getCloudFunction<{ uid: string }, { success: boolean; message: string }>('deleteUser');
      await deleteUserFunction({ uid: userKey });

    } catch (error) {
      console.error("Error deleting user:", error);
      throw error;
    }
  }
};

export default UserManager;